import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { Icons } from "../lib/Icons";

// Styled number badge component
function StepNumber({ number, variant = "indigo" }) {
  const variants = {
    indigo: "bg-indigo-600 text-white",
    purple: "bg-gradient-to-br from-purple-500 to-pink-500 text-white",
  };
  return (
    <div className={`w-10 h-10 rounded-xl ${variants[variant]} flex items-center justify-center font-bold text-lg shadow-lg`}>
      {number}
    </div>
  );
}

// ---------- Utility Components ----------
function Container({ children, className = "" }) {
  return (
    <div className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
  );
}

function SectionHeading({ eyebrow, title, subtitle, accent = "indigo" }) {
  const accentClass = accent === "purple" ? "text-purple-600" : "text-indigo-600";
  return (
    <div className="text-center max-w-3xl mx-auto">
      {eyebrow && (
        <p className={`text-sm font-semibold tracking-wider uppercase ${accentClass}`}>{eyebrow}</p>
      )}
      <h2 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">{title}</h2>
      {subtitle && (
        <p className="mt-3 text-base sm:text-lg text-gray-600 dark:text-gray-300">{subtitle}</p>
      )}
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium bg-white/60 backdrop-blur border-gray-200 dark:bg-gray-800/60 dark:border-gray-700">
      {children}
    </span>
  );
}

function FeatureCard({ icon: Icon, title, children }) {
  return (
    <div className="group rounded-2xl border border-slate-200/60 bg-white dark:bg-gray-800 p-6 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-indigo-100/40 hover:-translate-y-1 transition-all duration-300">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-100 to-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform duration-300">
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-gray-900 dark:text-white">{title}</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{children}</p>
        </div>
      </div>
    </div>
  );
}

// ---------- Email capture with Firestore backend ----------
function LeadCapture({ compact=false }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    
    setLoading(true);
    try {
      await addDoc(collection(db, 'leads'), {
        email: email,
        createdAt: serverTimestamp(),
        source: 'landing-page'
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Error saving lead:', error);
      alert('Sorry, there was an error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className={`${compact ? "" : "mt-6"} rounded-xl border bg-emerald-50 dark:bg-emerald-900/20 p-4 text-emerald-700 dark:text-emerald-200 flex items-center gap-2`}>
        <Icons.Check className="w-5 h-5" /> Thanks! We'll be in touch shortly.
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={`flex w-full ${compact? "gap-2" : "flex-col sm:flex-row gap-3"}`}>
      <input
        type="email"
        required
        value={email}
        onChange={(e)=>setEmail(e.target.value)}
        placeholder="Your work email"
        className="w-full rounded-xl border px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-600 dark:bg-gray-800 dark:border-gray-600 dark:text-white transition-shadow"
      />
      <button 
        type="submit" 
        disabled={loading}
        className="rounded-xl bg-indigo-600 px-5 py-3 font-semibold text-white hover:bg-indigo-700 hover:shadow-lg disabled:opacity-50 transition-all"
      >
        {loading ? "Sending..." : "Request demo"}
      </button>
    </form>
  );
}

// ---------- SEN‑AI Landing ----------
export default function SenAiLanding() {
  const navigate = useNavigate();
  const [showLoginOptions, setShowLoginOptions] = useState(false);
  const [showSignupOptions, setShowSignupOptions] = useState(false);
  const [howItWorksTab, setHowItWorksTab] = useState('schools');
  const loginRef = useRef(null);
  const signupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (loginRef.current && !loginRef.current.contains(event.target)) {
        setShowLoginOptions(false);
      }
      if (signupRef.current && !signupRef.current.contains(event.target)) {
        setShowSignupOptions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSchoolLogin = () => navigate('/login?mode=auth');
  const handleSoloLogin = () => navigate('/signup/solo?mode=login');
  const handleSchoolSignup = () => navigate('/login?mode=signup');
  const handleSoloSignup = () => navigate('/signup/solo');
  const handleSoloExplore = () => navigate('/solo/dashboard');
  const handleSchoolDemo = () => window.open('/demo', '_blank', 'noopener,noreferrer');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-gray-50 to-slate-100 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800" style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Background pattern - subtle cool gradient */}
      <div className="fixed inset-0 -z-10" aria-hidden="true">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(99,102,241,0.08),rgba(255,255,255,0))] dark:bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(99,102,241,0.25),rgba(0,0,0,0))]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_80%,rgba(139,92,246,0.04),transparent_50%)]"></div>
      </div>

      {/* Navigation Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200/60 bg-white/90 backdrop-blur-lg dark:bg-gray-900/80 shadow-sm shadow-slate-200/40">
        <Container className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <div className="flex items-center -space-x-2">
              <img
                src="/brand/logo-thumbnail-blue.png"
                alt=""
                className="h-9 w-9 rounded-lg ring-2 ring-white dark:ring-gray-900"
                aria-hidden="true"
              />
              <img
                src="/brand/logo-thumbnail-purple.png"
                alt="SEN-Ed"
                className="h-9 w-9 rounded-lg ring-2 ring-white dark:ring-gray-900"
              />
            </div>
            <span className="font-bold text-lg text-gray-900 dark:text-white">SEN-Ed</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#how" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">How it works</a>
            <a href="#features" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Features</a>
            <a href="#trust" className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Trust & GDPR</a>
            <a href="#pricing" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            {/* Login dropdown */}
            <div className="relative" ref={loginRef}>
              <button 
                onClick={() => { setShowLoginOptions(!showLoginOptions); setShowSignupOptions(false); }}
                className="rounded-xl border px-4 py-2 text-sm text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Log in
              </button>
              {showLoginOptions && (
                <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <button
                    onClick={handleSchoolLogin}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <Icons.School className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-sm">School Portal</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">For school staff</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={handleSoloLogin}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400">
                        <Icons.User className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-purple-600 dark:text-purple-400 text-sm">Individual Access</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Personal subscription</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
            
            {/* Get started dropdown */}
            <div className="relative" ref={signupRef}>
              <button 
                onClick={() => { setShowSignupOptions(!showSignupOptions); setShowLoginOptions(false); }}
                className="hidden sm:inline-block rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2 text-sm font-semibold text-white hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg transition-all"
              >
                Get started
              </button>
              {showSignupOptions && (
                <div className="absolute right-0 mt-2 w-72 rounded-xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700 p-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                  <button
                    onClick={handleSchoolDemo}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                        <Icons.School className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-sm">View Demo</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">See the full platform in action</p>
                      </div>
                    </div>
                  </button>
                  <div className="my-2 border-t border-gray-100 dark:border-gray-700"></div>
                  <button
                    onClick={handleSoloExplore}
                    className="w-full text-left px-4 py-3 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-lg shadow-purple-500/25">
                        <Icons.BookOpen className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent text-sm">Explore Resources</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Browse SEN teaching activities</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </Container>
      </header>

      {/* Hero - Dual Product Showcase */}
      <section className="relative py-20 sm:py-28 overflow-hidden">
        <Container>
          <div className="text-center mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight text-gray-900 dark:text-white">
              SEN support{" "}
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                made simple
              </span>
            </h1>
            <p className="mt-6 text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Whether you're a school team or an individual teacher, we have the tools you need to support students with SEND.
            </p>
          </div>

          {/* Side-by-side product cards */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Schools Card */}
            <div className="group rounded-3xl border border-indigo-200/60 dark:border-indigo-800/50 bg-gradient-to-br from-indigo-50/80 via-white to-blue-50/60 dark:from-indigo-900/10 dark:via-gray-800 dark:to-blue-900/10 p-8 relative overflow-hidden shadow-xl shadow-indigo-100/50 hover:shadow-2xl hover:shadow-indigo-200/40 hover:-translate-y-1 transition-all duration-500 animate-in fade-in slide-in-from-left-4 duration-700">
              {/* Decorative gradient */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-indigo-100 to-transparent dark:from-indigo-900/20 rounded-full -translate-y-32 translate-x-32 group-hover:scale-150 transition-transform duration-700"></div>
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shadow-xl shadow-indigo-500/30 group-hover:scale-110 transition-transform duration-300">
                      <Icons.School className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SEN-Ed Schools Platform</h2>
                      <p className="text-sm text-indigo-600 dark:text-indigo-400">Complete SEND provision management</p>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                    For Schools
                  </span>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                  Generate personalised curricula and evidence-rich reports in minutes, not weeks.
                </p>

                {/* Feature preview grid */}
                <div className="rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Document className="w-5 h-5 text-indigo-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">EHCP Analysis</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.ChartBar className="w-5 h-5 text-indigo-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Progress Tracking</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Brain className="w-5 h-5 text-indigo-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Curriculum Builder</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Clipboard className="w-5 h-5 text-indigo-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Evidence Reports</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSchoolDemo}
                  className="w-full rounded-xl bg-indigo-600 px-6 py-4 font-semibold text-white hover:bg-indigo-700 hover:shadow-xl hover:shadow-indigo-500/25 transition-all duration-300 text-lg group-hover:scale-[1.02]"
                >
                  View Demo
                </button>
              </div>
            </div>

            {/* Individual Teachers Card */}
            <div className="group rounded-3xl border border-purple-200/60 dark:border-purple-800/50 bg-gradient-to-br from-purple-50/80 via-white to-pink-50/60 dark:from-purple-900/10 dark:via-gray-800 dark:to-pink-900/10 p-8 relative overflow-hidden shadow-xl shadow-purple-100/50 hover:shadow-2xl hover:shadow-purple-200/60 hover:-translate-y-1 transition-all duration-500 animate-in fade-in slide-in-from-right-4 duration-700">
              {/* Decorative gradient */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-100 via-pink-100 to-transparent dark:from-purple-900/20 dark:via-pink-900/20 rounded-full -translate-y-32 translate-x-32 group-hover:scale-150 transition-transform duration-700"></div>
              
              <div className="relative">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-xl shadow-purple-500/30 group-hover:scale-110 transition-transform duration-300">
                      <Icons.BookOpen className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">SEN-Ed Resources</h2>
                      <p className="text-sm text-purple-600 dark:text-purple-400">Individual teacher access</p>
                    </div>
                  </div>
                  <span className="hidden sm:inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/50 px-3 py-1 text-xs font-medium text-purple-700 dark:text-purple-300">
                    For Teachers
                  </span>
                </div>

                <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                  Browse hundreds of SEN teaching activities and resources to support your students.
                </p>

                {/* Feature preview grid */}
                <div className="rounded-2xl bg-gradient-to-br from-purple-50/80 to-pink-50/80 dark:from-purple-900/10 dark:to-pink-900/10 p-6 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Search className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">AI Search</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Play className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Video Guides</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Puzzle className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">100+ Activities</span>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-white/80 dark:bg-gray-800/80 rounded-xl">
                      <Icons.Heart className="w-5 h-5 text-purple-500" />
                      <span className="text-sm text-gray-700 dark:text-gray-300">All SEN Needs</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSoloExplore}
                  className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 font-semibold text-white hover:from-purple-600 hover:to-pink-600 hover:shadow-xl hover:shadow-purple-500/25 transition-all duration-300 text-lg group-hover:scale-[1.02]"
                >
                  Explore Now
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* How it works - Tabbed */}
      <section id="how" className="py-20 sm:py-28 border-t border-slate-200/60 dark:border-gray-700">
        <Container>
          <SectionHeading
            eyebrow="How it works"
            title="Choose your path to SEN success"
            subtitle="Whether you're managing provision for a whole school or finding resources for your classroom."
          />

          {/* Tab switcher */}
          <div className="mt-12 flex justify-center">
            <div className="inline-flex rounded-2xl bg-gray-100 dark:bg-gray-800 p-1.5">
              <button
                onClick={() => setHowItWorksTab('schools')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  howItWorksTab === 'schools'
                    ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icons.School className="w-5 h-5" />
                Schools
              </button>
              <button
                onClick={() => setHowItWorksTab('individual')}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${
                  howItWorksTab === 'individual'
                    ? 'bg-white dark:bg-gray-700 text-purple-600 dark:text-purple-400 shadow-lg'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <Icons.User className="w-5 h-5" />
                Individual Teachers
              </button>
            </div>
          </div>

          {/* Schools flow */}
          {howItWorksTab === 'schools' && (
            <div className="mt-12 grid md:grid-cols-3 gap-8">
              {[
                { num: 1, title: "Import & Extract", desc: "Upload EHCPs and assessments. We extract Section E outcomes and map needs across the 4 areas of SEND.", icon: Icons.Upload },
                { num: 2, title: "Match & Plan", desc: "Our knowledge base selects activities by attainment level, communication style and individual needs profile.", icon: Icons.Puzzle },
                { num: 3, title: "Deliver & Evidence", desc: "Weekly plans for teachers, intervention tracking, and graduated response reports that stand up at panel.", icon: Icons.Clipboard },
              ].map((step, idx) => (
                <div key={idx} className="group rounded-2xl border border-slate-200/60 dark:border-indigo-900 bg-white dark:bg-gray-800 p-8 shadow-lg shadow-slate-200/50 hover:shadow-xl hover:shadow-indigo-100/40 hover:-translate-y-2 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-4">
                    <StepNumber number={step.num} variant="indigo" />
                    <div className="w-12 h-12 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                      <step.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 dark:text-white mb-3">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{step.desc}</p>
                </div>
              ))}
            </div>
          )}

          {/* Individual teachers flow */}
          {howItWorksTab === 'individual' && (
            <div className="mt-12 grid md:grid-cols-3 gap-8">
              {[
                { num: 1, title: "Browse & Search", desc: "Explore activities by domain, age group, or use AI-powered search to find exactly what you need.", icon: Icons.Search },
                { num: 2, title: "Find Resources", desc: "Video tutorials, buy/DIY resources, and step-by-step guides for every strategy and activity.", icon: Icons.Play },
                { num: 3, title: "Support Your Students", desc: "Ready-to-use strategies for any SEN need, from communication to sensory processing.", icon: Icons.Heart },
              ].map((step, idx) => (
                <div key={idx} className="group rounded-2xl border border-purple-200/60 dark:border-purple-900 bg-gradient-to-br from-white to-purple-50/40 dark:from-purple-900/10 dark:to-pink-900/10 p-8 shadow-lg shadow-purple-100/40 hover:shadow-xl hover:shadow-purple-200/50 hover:-translate-y-2 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-4">
                    <StepNumber number={step.num} variant="purple" />
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform">
                      <step.icon className="w-6 h-6" />
                    </div>
                  </div>
                  <h3 className="font-bold text-xl text-gray-900 dark:text-white mb-3">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300">{step.desc}</p>
                </div>
              ))}
            </div>
          )}
        </Container>
      </section>

      {/* Features */}
      <section id="features" className="py-20 sm:py-28 border-t border-slate-200/60 dark:border-gray-700">
        <Container>
          <SectionHeading
            eyebrow="Features"
            title="Everything a SENCO wishes lived in one place"
            subtitle="Purpose-built for mainstream and special settings."
          />
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={Icons.Brain} title="Personalised curriculum">
              Generate weekly overviews plus deep-dive day plans with strategies, outcomes and resources.
            </FeatureCard>
            <FeatureCard icon={Icons.Document} title="Graduated response reports">
              Auto-compile provision + impact evidence suitable for panels and Ofsted conversations.
            </FeatureCard>
            <FeatureCard icon={Icons.ChartBar} title="Intervention tracking">
              Group learners, schedule sessions, and monitor progress across cohorts.
            </FeatureCard>
            <FeatureCard icon={Icons.Search} title="Access arrangements support">
              Aggregate the evidence you need for JCQ applications without extra admin.
            </FeatureCard>
            <FeatureCard icon={Icons.Ruler} title="Pre-National Curriculum Support">
              Engagement model and pre-national curriculum activities designed for complex pupils working below age-related expectations.
            </FeatureCard>
            <FeatureCard icon={Icons.Shield} title="Student Data Protection">
              Your students' data stays safe with UK hosting, encryption, role-based access, and GDPR-compliant controls.
            </FeatureCard>
          </div>
        </Container>
      </section>

      {/* Trust */}
      <section id="trust" className="py-20 sm:py-28 border-t border-slate-200/60 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-800/30">
        <Container>
          <SectionHeading
            eyebrow="Trust"
            title="Designed for safeguarding and GDPR compliance"
            subtitle="We follow privacy-by-design, with audit logs, retention controls and DPIA templates for schools."
          />
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <FeatureCard icon={Icons.Lock} title="Secure by default">
              SSO, MFA, role-based permissions, and encryption in transit & at rest.
            </FeatureCard>
            <FeatureCard icon={Icons.DocumentCheck} title="Clear governance">
              Data Processing Agreement, UK-hosted infrastructure, and subject access workflows.
            </FeatureCard>
            <FeatureCard icon={Icons.Megaphone} title="Transparent operations">
              Status page, incident response and designated DPO contact.
            </FeatureCard>
          </div>
        </Container>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 sm:py-28 border-t border-slate-200/60 dark:border-gray-700">
        <Container>
          <SectionHeading
            eyebrow="Pricing"
            title="Choose how you want to access SEN resources"
            subtitle="Whether you're an individual teacher or a school team, we have the right plan for you."
          />
          
          <div className="mt-12 grid lg:grid-cols-2 gap-8">
            {/* School Plans Card */}
            <div className="group rounded-3xl border border-indigo-200/60 dark:border-indigo-800 bg-gradient-to-br from-white via-indigo-50/50 to-blue-50/50 dark:from-indigo-900/20 dark:to-blue-900/20 p-8 relative overflow-hidden shadow-lg shadow-indigo-100/50 hover:shadow-xl hover:shadow-indigo-200/40 hover:-translate-y-1 transition-all duration-300">
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center rounded-full bg-indigo-100 dark:bg-indigo-900/50 px-3 py-1 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                  Schools
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
                  <Icons.School className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">SEN-Ed Schools Platform</h3>
                  <p className="text-sm text-indigo-600 dark:text-indigo-400">Full school solution</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Complete SEND provision management with curriculum generation, intervention tracking, and evidence reports.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "Personalised curriculum generator",
                  "EHCP extraction & analysis",
                  "Intervention tracking & progress",
                  "Graduated response reports",
                  "Multi-user school accounts",
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Icons.Check className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">Contact us</span>
                <span className="text-gray-500 dark:text-gray-400">for school pricing</span>
              </div>
              <button
                onClick={handleSchoolDemo}
                className="w-full rounded-xl bg-indigo-600 px-6 py-3 font-semibold text-white hover:bg-indigo-700 hover:shadow-lg transition-all"
              >
                View Demo
              </button>
            </div>

            {/* Individual Teacher Card */}
            <div className="group rounded-3xl border border-purple-200/60 dark:border-purple-800 bg-gradient-to-br from-white via-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20 p-8 relative overflow-hidden shadow-lg shadow-purple-100/50 hover:shadow-xl hover:shadow-purple-200/60 hover:-translate-y-1 transition-all duration-300">
              <div className="absolute top-4 right-4">
                <span className="inline-flex items-center rounded-full bg-purple-100 dark:bg-purple-900/50 px-3 py-1 text-xs font-medium text-purple-700 dark:text-purple-300">
                  Individual
                </span>
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/25">
                  <Icons.BookOpen className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">SEN-Ed Resources</h3>
                  <p className="text-sm text-purple-600 dark:text-purple-400">For individual teachers</p>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Browse hundreds of evidence-based activities and resources to support your students with SEND.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  "100+ SEN teaching activities",
                  "Video resources for every strategy",
                  "Primary & Secondary sections",
                  "AI-powered resource search",
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <Icons.Check className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                onClick={handleSoloExplore}
                className="w-full rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-3 font-semibold text-white hover:from-purple-600 hover:to-pink-600 hover:shadow-lg transition-all"
              >
                Explore Now
              </button>
            </div>
          </div>
        </Container>
      </section>

      {/* Lead capture CTA */}
      <section id="lead" className="py-20 sm:py-28 border-t border-slate-200/60 dark:border-gray-700 bg-gradient-to-br from-indigo-50/40 via-slate-50/50 to-transparent dark:from-indigo-900/20">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeading
                eyebrow="For Schools"
                title="Want a 15-minute demo?"
                subtitle="Leave your email and we'll set you up with a sandbox and a quick call."
              />
              <div className="mt-8"><LeadCapture /></div>
            </div>
            <div className="lg:pl-10">
              <div className="rounded-3xl border border-purple-200/60 dark:border-purple-800 bg-gradient-to-br from-white via-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20 p-10 text-center shadow-xl shadow-purple-100/40">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-6 shadow-xl shadow-purple-500/25">
                  <Icons.User className="w-8 h-8 text-white" />
                </div>
                <p className="text-sm font-semibold tracking-wider uppercase text-purple-600">For Teachers</p>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 mt-2">Individual Teacher?</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-8">
                  Get instant access to hundreds of SEN resources with no demo required.
                </p>
                <button
                  onClick={handleSoloExplore}
                  className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4 font-semibold text-white hover:from-purple-600 hover:to-pink-600 hover:shadow-lg transition-all text-lg"
                >
                  Explore Now
                </button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200/60 dark:border-gray-700 py-12 text-sm text-gray-500 dark:text-gray-400">
        <Container className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img 
              src="/brand/logo-thumbnail-blue.png" 
              alt="SEN-Ed" 
              className="h-8 w-8 rounded-lg"
            />
            <span className="font-semibold text-gray-900 dark:text-white">SEN-Ed</span>
          </div>
          <p className="text-xs sm:text-sm text-center">
            © 2026 The SEND Tech Company Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="hover:text-indigo-600 transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-indigo-600 transition-colors">Terms</a>
            <a href="/security" className="hover:text-indigo-600 transition-colors">Security</a>
          </div>
        </Container>
      </footer>
    </div>
  );
}
